import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  Accordion,
  SimpleGrid
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCopy,
  IconCheck,
  IconDownload,
  IconExternalLink
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { TEMPLATES } from '../utils/templates';

const CATEGORY_COLORS = {
  'Smoke test': 'gray',
  'IVR menu': 'blue',
  'Routing': 'orange',
  'IVA / AI agent': 'violet'
};

const DIFFICULTY_COLORS = {
  Beginner: 'green',
  Intermediate: 'yellow',
  Advanced: 'red'
};

function TestTemplates({ onBack }) {
  const navigate = useNavigate();
  const [openTemplate, setOpenTemplate] = useState(null);

  const downloadYaml = (template) => {
    const blob = new Blob([template.yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.id}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const sendToRunTrigger = (template) => {
    navigate('/run', { state: { prefillYaml: template.yaml } });
  };

  return (
    <Container size="lg" py="lg">
      <Group mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
      </Group>

      <Stack gap="md">
        <Title order={2}>Test Plan Templates</Title>
        <Text c="dimmed">
          Ready-to-run sample plans for common test scenarios. Pick one, send it
          to the Run Trigger, fill in your destination DID, and start a real
          test call. Use these as starting points for your own plans — copy the
          YAML, edit the steps, and save your own variations.
        </Text>

        <SimpleGrid cols={{ base: 1, md: 2 }} gap="md">
          {TEMPLATES.map((tmpl) => (
            <Card key={tmpl.id} withBorder padding="lg" radius="md">
              <Stack gap="sm" h="100%">
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Text fw={600}>{tmpl.name}</Text>
                    <Group gap="xs">
                      <Badge size="xs" color={CATEGORY_COLORS[tmpl.category] ?? 'gray'} variant="light">
                        {tmpl.category}
                      </Badge>
                      <Badge size="xs" color={DIFFICULTY_COLORS[tmpl.difficulty] ?? 'gray'} variant="outline">
                        {tmpl.difficulty}
                      </Badge>
                    </Group>
                  </Stack>
                </Group>

                <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                  {tmpl.description}
                </Text>

                <Accordion variant="separated" value={openTemplate === tmpl.id ? 'yaml' : null} onChange={(v) => setOpenTemplate(v ? tmpl.id : null)}>
                  <Accordion.Item value="yaml">
                    <Accordion.Control>
                      <Text size="sm" fw={500}>View YAML</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Code block style={{ maxHeight: 300, overflow: 'auto', fontSize: 11 }}>
                        {tmpl.yaml}
                      </Code>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>

                <Group justify="space-between" gap="xs">
                  <CopyButton value={tmpl.yaml}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy YAML'}>
                        <ActionIcon variant="light" onClick={copy} color={copied ? 'green' : 'violet'} size="md">
                          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                  <Tooltip label="Download YAML">
                    <ActionIcon variant="light" color="violet" size="md" onClick={() => downloadYaml(tmpl)}>
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Button
                    flex={1}
                    size="sm"
                    color="violet"
                    leftSection={<IconExternalLink size={14} />}
                    onClick={() => sendToRunTrigger(tmpl)}
                  >
                    Use this template
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

export default TestTemplates;
